import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "../api/client";
import {
  type CatalogueAnswer,
  type CatalogueEntry,
  catalogue,
  type Settings,
} from "./api";
import { merge, type Prepared, prepare } from "./search";

/**
 * The catalogue index, held in the browser and kept fresh (M1.13 §2).
 *
 * Loaded **once per page load** and shared by every component that asks — the search on the show
 * page and the settings page read the same copy — then brought up to date with deltas: every
 * thirty seconds, and whenever the tab regains focus, which is the moment an operator coming back
 * to the panel is about to search.
 *
 * Module-level rather than in a context, because it is one list for the whole panel and its
 * lifetime is the page's, not any component's.
 */

/** How often the held index asks for what changed. */
export const CATALOGUE_REFRESH_MS = 30_000;

interface Held {
  entries: CatalogueEntry[];
  watermark: string | null;
  runs: CatalogueAnswer["runs"];
  settings: Settings;
}

let held: Held | undefined;
let inflight: Promise<Held> | undefined;
const listeners = new Set<(next: Held) => void>();

function publish(next: Held): void {
  held = next;
  for (const listener of listeners) listener(next);
}

async function load(): Promise<Held> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      if (!held) {
        const answer = await catalogue.read();
        publish({
          entries: answer.entries,
          watermark: answer.watermark,
          runs: answer.runs,
          settings: answer.settings,
        });
      } else {
        let answer = await catalogue.read(held.watermark ?? undefined);
        /*
          Every held entry's `breakEven` carries the addition it was served with, and a delta only
          brings the rows that changed — so when the addition itself has changed, the rest would
          keep the old figure until the page was reloaded. One full read puts them all right.
        */
        if (answer.settings.breakEvenAddition !== held.settings.breakEvenAddition) {
          answer = await catalogue.read();
          publish({
            entries: answer.entries,
            watermark: answer.watermark,
            runs: answer.runs,
            settings: answer.settings,
          });
          return held!;
        }
        publish({
          entries: merge(held.entries, answer.entries),
          watermark: answer.watermark ?? held.watermark,
          runs: answer.runs,
          settings: answer.settings,
        });
      }
      return held!;
    } finally {
      inflight = undefined;
    }
  })();
  return inflight;
}

export function useCatalogue() {
  const [state, setState] = useState<Held | undefined>(held);
  const [error, setError] = useState<string | undefined>(undefined);

  const refresh = useCallback(async () => {
    try {
      await load();
      setError(undefined);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : "unreachable");
    }
  }, []);

  useEffect(() => {
    let live = true;
    listeners.add(setState);
    // `.then` rather than `refresh`, the same shape `ShowsPage` uses: state is only ever set in a
    // callback, and only while this component is still mounted.
    const pull = () =>
      void load().then(
        () => {
          if (live) setError(undefined);
        },
        (failure: unknown) => {
          if (live)
            setError(failure instanceof ApiError ? failure.message : "unreachable");
        },
      );
    pull();
    const timer = setInterval(pull, CATALOGUE_REFRESH_MS);
    window.addEventListener("focus", pull);
    return () => {
      live = false;
      listeners.delete(setState);
      clearInterval(timer);
      window.removeEventListener("focus", pull);
    };
  }, []);

  /** Prepared once per change of the list, not once per keystroke. */
  const prepared: Prepared[] = useMemo(
    () => (state ? state.entries.map(prepare) : []),
    [state],
  );

  return {
    loaded: state !== undefined,
    entries: state?.entries ?? [],
    prepared,
    runs: state?.runs,
    settings: state?.settings,
    error,
    refresh,
  };
}
